export interface JWTPayload {
	sub: string;
	aud: string;
	role: string;
	email?: string;
	iat: number;
	exp: number;
}

/**
 * Check if a JWT token is expired (client-safe)
 */
export function isJWTExpired(token: string): boolean {
	try {
		if (!token || typeof token !== 'string') return true;

		const parts = token.split('.');
		if (parts.length !== 3) return true;

		// Decode the payload (second part)
		const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as JWTPayload;

		if (!payload.exp) return true;

		const now = Math.floor(Date.now() / 1000);
		const isExpired = payload.exp < now;

		// Debug logging
		if (isExpired) {
			console.debug('JWT expired check:', {
				tokenExp: payload.exp,
				now,
				diff: payload.exp - now,
				expDate: new Date(payload.exp * 1000).toISOString(),
				nowDate: new Date(now * 1000).toISOString()
			});
		}

		return isExpired;
	} catch (error) {
		console.error('Error checking JWT expiration:', error);
		return true;
	}
}

/**
 * Get user ID from JWT token safely (client-safe)
 */
export function getUserIdFromJWT(token: string): string | null {
	try {
		if (!token || typeof token !== 'string') return null;

		const parts = token.split('.');
		if (parts.length !== 3) return null;

		// Decode the payload (second part)
		const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as JWTPayload;

		return payload.sub || null;
	} catch (error) {
		console.error('Error extracting user ID from JWT:', error);
		return null;
	}
}

/**
 * Validate JWT token format without verification (for basic checks)
 */
export function isValidJWTFormat(token: string): boolean {
	if (!token || typeof token !== 'string') return false;

	const parts = token.split('.');
	if (parts.length !== 3) return false;

	// Check if parts are valid base64
	try {
		parts.forEach((part) => {
			atob(part.replace(/-/g, '+').replace(/_/g, '/'));
		});
		return true;
	} catch {
		return false;
	}
}

/**
 * Decode JWT payload without verification (client-safe)
 */
export function decodeJWTPayload(token: string): JWTPayload | null {
	try {
		if (!token || typeof token !== 'string') return null;

		const parts = token.split('.');
		if (parts.length !== 3) return null;

		// Decode the payload (second part)
		const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as JWTPayload;

		// Basic validation
		if (!payload.sub || !payload.aud || !payload.role) {
			return null;
		}

		return payload;
	} catch (error) {
		console.error('Error decoding JWT payload:', error);
		return null;
	}
}
